// src/composables/useAuthz.ts
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useCorporativeAccount } from '@/store/corporative'
import { useRoute } from 'vue-router'

type CheckMode = 'any' | 'all'

/**
 * Composable central de autorização
 * - Carrega contas corporativas e empresa atual (se necessário)
 * - Verifica permissões específicas
 * - Pode ser usado em PermissionGate, BaseLayouts, etc.
 */
export function useAuthz() {
  const route = useRoute()
  const corporative = useCorporativeAccount()
  const { accountPermissions, isAccountOwner, corporate, corporates } =
    storeToRefs(corporative)

  const loading = ref(false)
  const error = ref<unknown>(null)

  /** Garante que os dados básicos estejam carregados */
  async function ensureLoaded() {
    try {
      loading.value = true
      error.value = null

      // garante lista de corporates
      if (!corporates.value) {
        await corporative.getCorporates()
      }

      // garante corporate atual, se a rota tiver um :id
      const paramId = route.params.id
      const routeIdStr = Array.isArray(paramId)
        ? String(paramId[0])
        : String(paramId ?? '')

      if (routeIdStr) {
        const corpIdStr =
          corporate.value?.id != null ? String(corporate.value.id) : null

        // compara string com string (evita conflito number x string)
        if (!corpIdStr || corpIdStr !== routeIdStr) {
          await corporative.getCorporate(routeIdStr)
        }
      }
    } catch (e) {
      error.value = e
    } finally {
      loading.value = false
    }
  }

  /**
   * Verifica se o usuário tem as permissões exigidas
   * @param required string | string[]
   * @param mode 'any' (pelo menos uma) | 'all' (todas)
   * @param needsAdmin se true, somente owner passa
   */
  function hasPermissions(
    required?: string | string[],
    mode: CheckMode = 'any',
    needsAdmin = false
  ): boolean {
    // owner bypass
    if (isAccountOwner.value) return true
    if (needsAdmin) return false
    if (!required) return true

    const perms = accountPermissions.value ?? []

    if (typeof required === 'string') return perms.includes(required)

    // modo "any" ou "all"
    return mode === 'all'
      ? required.every((p) => perms.includes(p))
      : required.some((p) => perms.includes(p))
  }

  return {
    /** estado de carregamento */
    loading,
    /** último erro */
    error,
    /** se é owner */
    isAccountOwner: computed(() => isAccountOwner.value),
    /** todas permissões do usuário */
    accountPermissions,
    /** garante dados carregados */
    ensureLoaded,
    /** verifica permissões */
    hasPermissions,
  }
}
